// Боковое меню (п.20, финал переезда 2026-09-07): проект, разделы, выход,
// сворачивание. Состав и порядок пунктов — как в прежней оболочке.
import { useState } from 'react';
import { alertBox, ask, confirmBox } from '../dialog';
import { call, type Data } from '../api';

type Me = Data<'/api/auth/me', 'get'>;
type Project = Data<'/api/projects', 'get'>[number];

export type TabId =
  | 'obshchee' | 'plan' | 'ruch' | 'checks' | 'struct' | 'works' | 'podryad' | 'shemy' | 'links' | 'spiski'
  | 'resurs' | 'kvart' | 'fronty' | 'fakt' | 'faktlog' | 'rep' | 'users' | 'wiki';

type Item = { id: TabId; label: string; title?: string; hidden?: boolean };
/** ссылка на другое приложение (сайт шахматки) — не вкладка, открывается рядом */
type Vneshnyaya = { label: string; url: string; title?: string };
type Group = { key: string; label: string; items: Item[]; sub?: { key: string; label: string; items: Item[] };
               admin?: boolean; vneshnie?: Vneshnyaya[] };

export const GROUPS: Group[] = [
  { key: 'grpSprav', label: 'Справочники', items: [
      { id: 'struct', label: 'Этажи' },
      { id: 'works', label: 'Работы' },
      // справочник подрядчиков проекта (решение владельца 2026-09-14); в «Глобально» не показывается
      { id: 'podryad', label: 'Подрядчики', title: 'Подрядчики проекта: контакты, на каких корпусах какие работы ведут, привязанные пользователи' },
      // чертежи для графического планирования (ТЗ 2026-09-19, #104); в «Глобально» не показываются
      { id: 'shemy', label: 'Схемы', title: 'Чертежи проекта: план паркинга и этажей, на которых размечаются захватки' },
      { id: 'links', label: 'Предшественники', title: 'Схема предшественников проекта: та же информация, что тэги в «Работах»' },
      { id: 'spiski', label: 'Списки' },
    ], sub: { key: 'grpDop', label: 'Доп. справочники', items: [
      { id: 'resurs', label: 'Ресурсы' }, { id: 'kvart', label: 'Квартиры' },
    ] } },
  { key: 'grpPlan', label: 'Планирование', items: [
      // «Общее» (владелец 2026-09-15, #96): старт проекта и сводка по этапам
      { id: 'obshchee', label: 'Общее', title: 'Старт проекта и сводка по этапам работ: начало, окончание' },
      { id: 'plan', label: 'Модель ГПР' },
      { id: 'checks', label: 'Проверки' },
    ] },
  { key: 'grpFakt', label: 'Факт', items: [
      { id: 'fronty', label: 'Открытые фронты', title: 'Что можно брать в работу сейчас: предшественники закончены по факту' },
      { id: 'fakt', label: 'Шахматка факта' },
      { id: 'faktlog', label: 'Журнал' },
      // «Отчёты» скрыты 2026-09-06 (решение владельца), экран цел
      { id: 'rep', label: 'Отчёты', hidden: true },
    ],
    // сайт шахматки подрядчиков — отдельное приложение на /fakt/ (2026-09-10):
    // открывается рядом, вход тот же, планер видит ровно то, что видят они
    vneshnie: [{ label: 'Шахматка подрядчиков ↗', url: '/fakt/',
                 title: 'Открыть сайт шахматки подрядчиков в новой вкладке: то же, что видят они. '
                      + 'Вход тот же — заново входить не нужно' }] },
  { key: 'grpAdmin', label: 'Администрирование', admin: true, items: [{ id: 'users', label: 'Пользователи' }] },
  { key: 'grpWiki', label: 'Справка', items: [{ id: 'wiki', label: 'Как считаются сроки' }] },
];
/** что доступно в проекте «Глобально» (общие справочники) */
const GLOBAL_TABS: TabId[] = ['works', 'links', 'spiski', 'users', 'wiki'];
export const TABS: Item[] = GROUPS.flatMap(g => g.items.concat(g.sub?.items || []));

export function Menu({ tab, onTab, me, role, isGlobal, projects, pid, onProject, onNewProject, onLogout, onLogoutAll }: {
  tab: TabId; onTab: (t: TabId) => void; me: Me; role: string | null; isGlobal: boolean;
  projects: Project[]; pid: number | null;
  onProject: (id: number) => void; onNewProject: (id: number) => void; onLogout: () => void;
  onLogoutAll: () => void;
}) {
  const [mini, setMini] = useState(() => {
    try { return localStorage.getItem('plot_side') === '1'; } catch { return false; }
  });
  function toggle() {
    setMini(v => {
      const next = !v;
      try { localStorage.setItem('plot_side', next ? '1' : ''); } catch { /* приватный режим */ }
      document.getElementById('appView')?.classList.toggle('mini', next);
      return next;
    });
  }
  // класс на слое отвечает за узкое меню (стили оболочки)
  document.getElementById('appView')?.classList.toggle('mini', mini);

  async function newProject() {
    const name = (await ask('Название нового проекта:') || '').trim();
    if (!name) return;
    // библиотека-шаблон (#7): работы, связи и списки из «Глобально»
    const iz = await confirmBox('Взять работы, связи и списки из «Глобально»?',
      { podpis: 'Нет — проект будет пустым, работы можно добавить из «Глобально» позже по одной.', ok: 'Взять' });
    const r = await call<{ id: number }>('post', '/api/projects', { name, iz_globalno: iz });
    if (!r.ok) { await alertBox(r.error.message); return; }
    try { localStorage.setItem('plot_pid', String(r.data.id)); } catch { /* приватный режим */ }
    onNewProject(r.data.id);
  }

  const visible = (i: Item) => !i.hidden && (!isGlobal || GLOBAL_TABS.includes(i.id));
  const link = (i: Item) => (
    <a key={i.id} className={tab === i.id ? 'on' : ''} title={i.title}
      onClick={() => onTab(i.id)}>
      {isGlobal && i.id === 'links' ? 'Шаблоны связей' : i.label}
    </a>
  );

  return (
    <aside className="side">
      <div className="side-top">
        <div className="brand">КАСКАД</div>
        <a className="side-tgl" title="Свернуть/развернуть меню" onClick={toggle}>{mini ? '»' : '«'}</a>
      </div>
      <div className="who" title={me.org ? 'Организация: ' + me.org : undefined}>
        {me.name + (me.org ? ' · ' + me.org : '') + (role ? ' · ' + role : '')}</div>
      <div className="proj">
        <select value={pid ?? ''} title="Проект" onChange={e => onProject(+e.target.value)}>
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        {me.is_admin && <button title="Создать новый проект (администратор)" onClick={() => void newProject()}>＋</button>}
      </div>
      {GROUPS.map(g => {
        if (g.admin && !me.is_admin) return null;
        const items = g.items.filter(visible);
        const sub = g.sub ? g.sub.items.filter(visible) : [];
        // ссылки на соседние приложения в проекте «Глобально» не нужны: там нет факта
        const vnesh = isGlobal ? [] : (g.vneshnie || []);
        if (!items.length && !sub.length && !vnesh.length) return null;
        return (
          <details key={g.key} open>
            <summary className="sec-h">{g.label}</summary>
            {items.map(link)}
            {g.sub && sub.length > 0 && (
              <details key={g.sub.key}>
                <summary className="sec-h">{g.sub.label}</summary>
                {sub.map(link)}
              </details>
            )}
            {vnesh.map(v => (
              <a key={v.url} href={v.url} target="_blank" rel="noopener noreferrer" title={v.title}>
                {v.label}
              </a>
            ))}
          </details>
        );
      })}
      <div className="side-foot">
        <a onClick={onLogout}>Выйти</a>
        {!mini && <a onClick={onLogoutAll} style={{ fontSize: 12, opacity: .7 }}
          title="Погасить все свои входы, кроме этого — если ссылка с доступом куда-то попала">
          Выйти везде</a>}
      </div>
    </aside>
  );
}
